import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchAdminUsers, updateAdminUser } from '../api/adminUsers';
import type { UserRole } from '../api/types';
import { ErrorState } from '../components/ErrorState';
import { LoadingState } from '../components/LoadingState';

const roles: UserRole[] = ['MEMBER', 'MODERATOR', 'ADMINISTRATOR', 'SYSOP'];

export function AdminUsersPage() {
  const queryClient = useQueryClient();
  const [banReasons, setBanReasons] = useState<Record<string, string>>({});
  const usersQuery = useQuery({ queryKey: ['admin', 'users'], queryFn: fetchAdminUsers });
  const updateMutation = useMutation({
    mutationFn: ({ id, role, isBanned, banReason }: { id: string; role: UserRole; isBanned: boolean; banReason?: string | null }) =>
      updateAdminUser(id, { role, isBanned, banReason }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    }
  });

  if (usersQuery.isLoading) return <div className="container-fluid px-3 px-lg-4 py-4"><LoadingState label="Loading users" /></div>;
  if (usersQuery.isError) return <div className="container-fluid px-3 px-lg-4 py-4"><ErrorState title="Unable to load users" error={usersQuery.error} /></div>;

  return <div className="container-fluid px-3 px-lg-4 py-4">
    <div className="d-flex justify-content-between align-items-center mb-3"><h1 className="h3 mb-0">Admin User Management</h1></div>
    <div className="table-responsive"><table className="table table-sm table-striped align-middle">
      <thead><tr><th>User</th><th>Role</th><th>Ban</th><th>Action</th></tr></thead>
      <tbody>{(usersQuery.data ?? []).map((user) => (
        <tr key={user.id}>
          <td><div className="fw-semibold">{user.username}</div><div className="text-body-secondary small">{user.discordId ?? user.id}</div></td>
          <td><select className="form-select form-select-sm" defaultValue={user.role} onChange={(event) => void updateMutation.mutateAsync({ id: user.id, role: event.target.value as UserRole, isBanned: user.isBanned, banReason: user.banReason })}>{roles.map((role) => <option key={role} value={role}>{role}</option>)}</select></td>
          <td>
            <div className="form-check form-switch"><input className="form-check-input" type="checkbox" checked={user.isBanned} onChange={(event) => void updateMutation.mutateAsync({ id: user.id, role: user.role, isBanned: event.target.checked, banReason: banReasons[user.id] ?? user.banReason })} /></div>
            {user.isBanned ? <div className="small text-danger">{user.banReason ?? 'Banned'}</div> : null}
            <input className="form-control form-control-sm mt-1" placeholder="Ban reason" value={banReasons[user.id] ?? user.banReason ?? ''} onChange={(event) => setBanReasons((prev) => ({ ...prev, [user.id]: event.target.value }))} />
          </td>
          <td><button className="btn btn-outline-primary btn-sm" onClick={() => void updateMutation.mutateAsync({ id: user.id, role: user.role, isBanned: user.isBanned, banReason: banReasons[user.id] ?? user.banReason })}>Save</button></td>
        </tr>
      ))}</tbody>
    </table></div>
  </div>;
}
